import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumbs, Container, Link, Text } from '@hopskipdrive/react-components';
import styled from 'styled-components';

import { theme } from '../../shared/components/theme';
import { colorPalette, colorTokens } from '../../shared/components/variables';
import { useAlertsDetectOriginProvider } from '../../shared/contexts/AlertsDetectOriginContext';
import { useFlags } from '../../shared/contexts/LDContext';
import { useToast } from '../../shared/contexts/ToastContext';
import { ErrorBoundary } from '../../shared/utils/errorReporting/ErrorBoundary';
import { useIsMobileOrTablet } from '../../shared/utils/helpers/util';
import { AlertNavDrawer } from '../features/AlertNav';
import { SrsPanel } from '../features/AlertNav/SrsPanel';
import { InfoNavDrawer } from '../features/InfoNav';
import { DriverDetailsTab } from '../features/InfoNav/DriverDetailsTab';
import { MoreTab } from '../features/InfoNav/MoreTab/Index';
import { OrganizerDetailsTab } from '../features/InfoNav/OrganizerDetailsTab';
import { RideOverviewTab } from '../features/InfoNav/RideOverviewTab';
import { RiderDetailsTab } from '../features/InfoNav/RiderDetailsTab';
import { HeadingSkeleton, PageHeading } from '../features/PageHeading';
import { ALERTS_HEADING_HEIGHT, HEADING_HEIGHT, HEADING_MAX_HEIGHT } from '../features/PageHeading/constants';
import { AlertsHeading } from '../features/PageHeading/PageHeading.styles';
import { MapLoader, RouteMap } from '../features/RouteMap';
import { useLiveAlertsQuery } from '../hooks/useLiveAlertsQuery';
import { useLiveRideUpdates } from '../hooks/useLiveRideUpdates';
import { useRideDetailsQuery } from '../hooks/useRideDetailsQuery';
import { useRideOverviewQuery } from '../hooks/useRideOverviewQuery';

const RideDetails: React.FC = () => {
  const { rideSlug } = useParams();
  const { data: rideDetailsResult, isFetching } = useRideDetailsQuery({ rideSlug: rideSlug || '' });
  const { data: liveAlertsDetailsResult, isFetching: isFetchingLiveAlerts } = useLiveAlertsQuery({
    rideSlug: rideSlug || '',
  });
  const { data: rideOverviewResult } = useRideOverviewQuery({ rideSlug: rideSlug || '' });
  const [drawersOpen, setDrawersOpen] = useState<string[]>(['alertNav']);
  const [selectedTab, setSelectedTab] = useState<string>('');
  const removedRiderSlugs = rideDetailsResult?.data?.removedRiders?.map((rider) => rider.slug) || [];
  const hasAllRidersRemoved =
    rideDetailsResult?.data?.riders?.filter((rider) => !removedRiderSlugs?.includes(rider.slug)).length === 0;
  const isMobileOrTablet = useIsMobileOrTablet();
  const { isAlertsReferrer } = useAlertsDetectOriginProvider();
  const { addToast, resetToasts } = useToast();
  const { enableOssCarePartnerRideDetails } = useFlags();

  useLiveRideUpdates({
    rideSlug: rideSlug || '',
    rideUuid: rideDetailsResult?.data?.uuid,
    rideState: rideDetailsResult?.data?.state || '',
  });

  const handleDrawerOpen = (drawerName) => {
    if (isMobileOrTablet) {
      drawersOpen?.indexOf(drawerName) !== -1 ? setDrawersOpen([]) : setDrawersOpen([drawerName]);
    } else {
      setDrawersOpen((prev) => {
        if (prev.indexOf(drawerName) !== -1) {
          return prev.filter((drawer) => drawer !== drawerName);
        }

        return [...prev, drawerName];
      });
    }
  };

  useEffect(() => {
    enableOssCarePartnerRideDetails &&
      rideDetailsResult?.data?.isCarePartnerRide &&
      addToast({
        severity: 'info',
        message: 'CarePartner Ride',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        autoHideDuration: null,
        showCloseButton: false,
        alertProps: {
          icon: false,
          sx: {
            backgroundColor: colorPalette.plum950,
            color: colorPalette.plum250,
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '27px',
            border: `1px solid ${colorPalette.plum550}`,
            gap: '100px',
          },
          action: (
            <Link
              data-testid="admin-link"
              target="_blank"
              href={rideDetailsResult?.data?.adminRideUrl}
              style={{
                textDecoration: 'none',
              }}
            >
              <Text variant="button" style={{ color: colorPalette.plum250 }}>
                OPEN IN ADMIN
              </Text>
            </Link>
          ),
        },
      });
  }, [rideDetailsResult?.data, enableOssCarePartnerRideDetails]);

  useEffect(() => {
    // Reset toasts if the ride is a CarePartner ride and the feature flag was turned off
    !enableOssCarePartnerRideDetails && rideDetailsResult?.data?.isCarePartnerRide && resetToasts();
  }, [rideDetailsResult?.data, enableOssCarePartnerRideDetails]);

  return (
    <>
      {isAlertsReferrer && (
        <AlertsHeading
          $isCarePartnerRide={enableOssCarePartnerRideDetails && rideDetailsResult?.data?.isCarePartnerRide}
        >
          <Breadcrumbs data-testid="breadcrumbs-nav" aria-label="breadcrumb">
            <Link data-testid="crumb-item" onClick={() => window.location.assign(document.referrer)}>
              <Text
                variant="h6"
                style={{
                  color: colorTokens.text.contrast,
                }}
              >
                Alerts Dashboard
              </Text>
            </Link>
          </Breadcrumbs>
        </AlertsHeading>
      )}
      <ErrorBoundary FallbackComponent={HeadingSkeleton}>
        <PageHeading
          isCarePartnerRide={enableOssCarePartnerRideDetails && rideDetailsResult?.data?.isCarePartnerRide}
          rideDetails={rideDetailsResult?.data}
          isFetching={isFetching}
          onMapButtonClick={() => {
            setDrawersOpen([]);
          }}
        />
      </ErrorBoundary>
      <Main
        isLeftDrawerOpen={!isMobileOrTablet && drawersOpen.indexOf('alertNav') !== -1}
        isRightDrawerOpen={!isMobileOrTablet && drawersOpen.indexOf('infoNav') !== -1}
      >
        <ErrorBoundary FallbackComponent={() => <MapLoader isLoading={true} />}>
          <RouteMap
            isFetching={isFetching}
            plannedRoute={rideDetailsResult?.data?.mapDetails?.plannedRoutePolyline}
            orderedWaypointCoordinates={rideDetailsResult?.data?.mapDetails?.orderedWaypointCoordinates}
            riders={rideDetailsResult?.data?.riders}
            waypointsAttributes={rideDetailsResult?.data?.editRideDetails?.waypointsAttributes}
            ridersInCar={rideOverviewResult?.data?.routeDetails
              ?.filter((waypoint) => waypoint.arrivalDetails?.actual)
              ?.map((waypoint) => waypoint.riders)
              ?.flat()}
            hasAlertsBannner={isAlertsReferrer}
          />
        </ErrorBoundary>
      </Main>
      <DrawerContainer $isAlertsReferrer={isAlertsReferrer}>
        <AlertNavDrawer
          rideLiveAlerts={liveAlertsDetailsResult?.data}
          isFetching={isFetchingLiveAlerts}
          timeZone={rideDetailsResult?.data?.timeZone}
          handleOpenDrawer={() => handleDrawerOpen('alertNav')}
          isDrawerOpen={drawersOpen.indexOf('alertNav') !== -1}
        >
          <SrsPanelContainer>
            <SrsPanel />
          </SrsPanelContainer>
        </AlertNavDrawer>
        <InfoNavDrawer
          isDrawerOpen={drawersOpen.indexOf('infoNav') !== -1}
          handleOpenDrawer={() => handleDrawerOpen('infoNav')}
          selectedTab={selectedTab}
          handleSelectTab={setSelectedTab}
        >
          {selectedTab === 'driver-details' && <DriverDetailsTab rideDetails={rideDetailsResult?.data} />}
          {selectedTab === 'riders-details' && (
            <RiderDetailsTab
              adminRideUrl={rideDetailsResult?.data?.adminRideUrl}
              riders={
                enableOssCarePartnerRideDetails
                  ? rideDetailsResult?.data?.riders.filter(
                      (rider) => !rider.isRiderAssistant && !removedRiderSlugs.includes(rider.slug)
                    )
                  : rideDetailsResult?.data?.riders.filter((rider) => !removedRiderSlugs.includes(rider.slug))
              }
              organizers={rideDetailsResult?.data?.organizers}
              removedRiders={rideDetailsResult?.data?.removedRiders}
              isCarePartnerRide={enableOssCarePartnerRideDetails && rideDetailsResult?.data?.isCarePartnerRide}
              riderAssistant={rideDetailsResult?.data?.riderAssistant}
              removedRiderAssistants={rideDetailsResult?.data?.removedRiderAssistants}
            />
          )}
          {selectedTab === 'organizer-details' && (
            <OrganizerDetailsTab organizers={rideDetailsResult?.data?.organizers} />
          )}
          {selectedTab === 'ride-overview' && (
            <RideOverviewTab
              rideState={rideDetailsResult?.data?.state}
              hasAllRidersRemoved={hasAllRidersRemoved}
              isCarePartnerRide={enableOssCarePartnerRideDetails && rideDetailsResult?.data?.isCarePartnerRide}
            />
          )}
          {selectedTab === 'more' && (
            <MoreTab
              adminUrl={rideDetailsResult?.data?.adminRideUrl}
              rideSlug={rideSlug || ''}
              timeZone={rideDetailsResult?.data?.timeZone}
            />
          )}
        </InfoNavDrawer>
      </DrawerContainer>
    </>
  );
};

export default RideDetails;

const Main = styled.div<{
  isLeftDrawerOpen?: boolean;
  isRightDrawerOpen?: boolean;
}>(({ isLeftDrawerOpen, isRightDrawerOpen }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: 0,
  ...(isLeftDrawerOpen && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: '720px',
  }),
  ...(isRightDrawerOpen && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: '720px',
  }),
}));

const DrawerContainer = styled.div<{ $isAlertsReferrer?: boolean }>`
  display: flex;
  flex-direction: row;
  bottom: 0;
  position: absolute;
  width: 100%;
  max-height: ${({ $isAlertsReferrer }) =>
    $isAlertsReferrer
      ? `calc(100vh - ${HEADING_HEIGHT}px)`
      : `calc(100vh - ${HEADING_HEIGHT}px + ${ALERTS_HEADING_HEIGHT}px)`};
  overflow: hidden;
  justify-content: space-between;
  z-index: 100;
  pointer-events: none;

  .MuiAccordionSummary-root {
    flex-direction: column-reverse;
    padding: 0;
  }
  .MuiAccordionSummary-content {
    margin: 0;
    width: 100%;
  }
  .MuiAccordionSummary-expandIconWrapper {
    width: 21px;
  }
  .MuiAccordion-root {
    overflow-y: visible;
    width: 720px;
    border-radius: 22.105px 22.105px 0px 0px !important;
    margin-top: auto;
    box-sizing: border-box;
    box-shadow: 1.105px 0px 4.421px 0px rgba(0, 0, 0, 0.25);
    pointer-events: all;

    z-index: 101;
    &.Mui-expanded {
      margin: 0 !important;
    }
  }

  @media (max-width: 1440px) {
    flex-direction: column-reverse;
    overflow: unset;

    .MuiAccordion-root {
      width: 100%;
    }
    .MuiTab-root {
      width: 19vw;
    }
  }
`;

const SrsPanelContainer = styled(Container)`
  min-height: calc(100vh - ${HEADING_HEIGHT}px);
  width: 100%;

  @media (max-width: 1440px) {
    min-height: calc(100vh - ${HEADING_MAX_HEIGHT}px);
  }
`;
